/**
 * DisplaySettingsPanel — Unified 3-section settings panel.
 *
 * Sections: INTERFACE (mode toggle) / TEXTE (text mode) / PARAMÈTRES GLOBAUX (4 toggles).
 * Used in sidebar (compact) and profile page (full).
 *
 * @since Display Settings v1
 */

import { useInterfaceMode, type InterfaceMode } from "@/hooks/useInterfaceMode";
import { useAdvancedUnlock } from "@/hooks/useAdvancedUnlock";
import { useModuleUnlock } from "@/hooks/useModuleUnlock";
import { useTextMode, type TextMode } from "@/hooks/useTextMode";
import { useDisplaySettings } from "@/hooks/useDisplaySettings";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useState } from "react";
import {
  Minus, Square, LayoutGrid, Lock, Sparkles, Type,
  CheckCircle2, Circle, ArrowRight, Eye, BarChart3, Minimize2, Zap,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";

// ── Interface mode config ──
const INTERFACE_MODES: { value: InterfaceMode; label: string; icon: typeof Minus }[] = [
  { value: "simple", label: "Simplifié", icon: Minus },
  { value: "core", label: "Standard", icon: Square },
  { value: "advanced", label: "Avancé", icon: LayoutGrid },
];

const MODE_DESCRIPTIONS: Record<InterfaceMode, string> = {
  simple: "Essentiel uniquement — interface épurée, moins de données",
  core: "Toutes les infos clés — le mode par défaut",
  advanced: "✦ Modules optionnels débloqués — exploration complète",
};

const LOCK_LABELS: Record<string, string> = {
  core: "1 action suffit : programme, séance ou planning",
  advanced: "Maîtrise les bases pour débloquer",
};

// ── Text mode config ──
const TEXT_MODES: { value: TextMode; label: string }[] = [
  { value: "simple", label: "Simplifié" },
  { value: "normal", label: "Standard" },
];

interface DisplaySettingsPanelProps {
  variant?: "compact" | "full";
}

export function DisplaySettingsPanel({ variant = "compact" }: DisplaySettingsPanelProps) {
  const { mode, setMode, isStandardUnlocked, isAdvancedUnlocked } = useInterfaceMode();
  const { requirements, completedCount, totalCount } = useAdvancedUnlock();
  const { activeCount } = useModuleUnlock();
  const { textMode, setTextMode } = useTextMode();
  const ds = useDisplaySettings();
  const navigate = useNavigate();
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);

  const isLocked = (m: InterfaceMode) => {
    if (m === "core" && !isStandardUnlocked) return true;
    if (m === "advanced" && !isAdvancedUnlocked) return true;
    return false;
  };

  const handleSetMode = (m: InterfaceMode) => {
    if (m === "advanced" && !isAdvancedUnlocked) {
      setShowAdvancedPanel((v) => !v);
      return;
    }
    if (isLocked(m)) return;
    setMode(m);
    setShowAdvancedPanel(false);
  };

  if (variant === "compact") {
    return (
      <div className="space-y-2.5 px-1">
        {/* ── INTERFACE ── */}
        <div>
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-0.5">
            Interface
          </span>
          <div className="flex gap-0.5 mt-1">
            {INTERFACE_MODES.map((m) => {
              const locked = isLocked(m.value);
              const isAdv = m.value === "advanced";
              return (
                <button
                  key={m.value}
                  onClick={() => handleSetMode(m.value)}
                  title={locked ? `${m.label} — ${LOCK_LABELS[m.value]}` : m.label}
                  className={cn(
                    "flex-1 text-[10px] font-medium py-1.5 rounded transition-colors flex items-center justify-center gap-0.5 min-h-[32px]",
                    mode === m.value
                      ? isAdv
                        ? "bg-primary text-primary-foreground ring-1 ring-primary/50"
                        : "bg-primary text-primary-foreground"
                      : m.value === "simple"
                      ? "text-muted-foreground hover:bg-muted/50 font-normal"
                      : locked
                      ? "text-muted-foreground/50 cursor-not-allowed"
                      : isAdv
                      ? "text-muted-foreground hover:bg-accent hover:text-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                  disabled={locked && m.value !== "advanced"}
                >
                  {locked && <Lock className="h-2.5 w-2.5" />}
                  {isAdv && !locked && <Sparkles className="h-2.5 w-2.5" />}
                  {m.label}
                </button>
              );
            })}
          </div>
          <p className="text-[9px] text-muted-foreground italic text-center mt-1">
            {MODE_DESCRIPTIONS[mode]}
          </p>
          {showAdvancedPanel && !isAdvancedUnlocked && (
            <AdvancedUnlockMini requirements={requirements} completedCount={completedCount} totalCount={totalCount} navigate={navigate} />
          )}
        </div>

        <Separator className="opacity-50" />

        {/* ── TEXTE ── */}
        <div>
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-0.5">
            Texte
          </span>
          <div className="flex gap-0.5 mt-1">
            {TEXT_MODES.map((m) => (
              <button
                key={m.value}
                onClick={() => setTextMode(m.value)}
                className={cn(
                  "flex-1 text-[10px] font-medium py-1.5 rounded transition-colors min-h-[32px]",
                  textMode === m.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <Separator className="opacity-50" />

        {/* ── PARAMÈTRES GLOBAUX ── */}
        <div className="space-y-1.5">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-0.5">
            Paramètres globaux
          </span>
          <CompactToggle label="Sources" checked={ds.showSources} onChange={ds.setShowSources} />
          <CompactToggle label="Stats détaillées" checked={ds.showDetailedStats} onChange={ds.setShowDetailedStats} />
          <CompactToggle label="Compact" checked={ds.compactMode} onChange={ds.setCompactMode} />
          <CompactToggle label="Animations réduites" checked={ds.reducedMotion} onChange={ds.setReducedMotion} />
        </div>
      </div>
    );
  }

  // ── Full variant ──
  return (
    <div className="space-y-5">
      {/* ── INTERFACE ── */}
      <div className="space-y-2">
        <SectionLabel>Interface</SectionLabel>
        <div className="grid gap-2">
          {INTERFACE_MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.value;
            const locked = isLocked(m.value);
            const isAdv = m.value === "advanced";
            const showSep = m.value === "advanced" && isStandardUnlocked;

            return (
              <div key={m.value}>
                {showSep && (
                  <div className="flex items-center gap-2 py-2">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      Modules optionnels disponibles ✦
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                )}
                <button
                  onClick={() => handleSetMode(m.value)}
                  disabled={locked && m.value !== "advanced"}
                  className={cn(
                    "flex items-center gap-3 rounded-lg border p-3 text-left transition-colors w-full min-h-[44px]",
                    active
                      ? isAdv
                        ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                        : "border-primary bg-primary/5"
                      : m.value === "simple"
                      ? "border-border bg-card hover:bg-muted/50"
                      : locked
                      ? "border-border bg-muted/30 opacity-80 cursor-pointer"
                      : "border-border bg-card hover:bg-accent/50"
                  )}
                >
                  <div className={cn(
                    "h-8 w-8 rounded-md flex items-center justify-center shrink-0",
                    active ? "bg-primary text-primary-foreground"
                      : locked ? "bg-muted text-muted-foreground"
                      : "bg-secondary text-muted-foreground"
                  )}>
                    {locked ? <Lock className="h-4 w-4" /> : isAdv ? <Sparkles className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className={cn(
                        "text-sm",
                        active && "text-primary",
                        m.value === "simple" ? "font-normal" : "font-medium"
                      )}>
                        {isAdv ? "✦ " : ""}{m.label}
                        {isAdv && active && activeCount > 0 && (
                          <span className="text-muted-foreground font-normal text-xs ml-1">· {activeCount} actifs</span>
                        )}
                      </p>
                      {locked && (
                        <span className="text-[9px] bg-muted text-muted-foreground rounded px-1 py-0.5 font-medium">
                          {LOCK_LABELS[m.value]}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{MODE_DESCRIPTIONS[m.value]}</p>
                  </div>
                  {active && <div className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                </button>
              </div>
            );
          })}
        </div>
        {showAdvancedPanel && !isAdvancedUnlocked && (
          <AdvancedUnlockMini requirements={requirements} completedCount={completedCount} totalCount={totalCount} navigate={navigate} />
        )}
      </div>

      <Separator />

      {/* ── TEXTE ── */}
      <div className="space-y-2">
        <SectionLabel>Texte</SectionLabel>
        <div className="grid gap-2">
          {TEXT_MODES.map((m) => {
            const active = textMode === m.value;
            return (
              <button
                key={m.value}
                onClick={() => setTextMode(m.value)}
                className={cn(
                  "flex items-center gap-3 rounded-lg border p-3 text-left transition-colors min-h-[44px]",
                  active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-accent/50"
                )}
              >
                <div className={cn(
                  "h-8 w-8 rounded-md flex items-center justify-center shrink-0",
                  active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                )}>
                  <Type className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-medium", active && "text-primary")}>{m.label}</p>
                </div>
                {active && <div className="h-2 w-2 rounded-full bg-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      <Separator />

      {/* ── PARAMÈTRES GLOBAUX ── */}
      <div className="space-y-3">
        <div>
          <SectionLabel>Paramètres globaux</SectionLabel>
          <p className="text-xs text-muted-foreground mt-0.5">
            Ces réglages s'appliquent partout
          </p>
        </div>
        <FullToggle
          icon={<Eye className="h-4 w-4" />}
          label="Afficher les sources"
          description="Montre les citations sous les faits scientifiques"
          checked={ds.showSources}
          onChange={ds.setShowSources}
        />
        <FullToggle
          icon={<BarChart3 className="h-4 w-4" />}
          label="Afficher les stats détaillées"
          description="KPIs secondaires, pourcentages, graphiques"
          checked={ds.showDetailedStats}
          onChange={ds.setShowDetailedStats}
        />
        <FullToggle
          icon={<Minimize2 className="h-4 w-4" />}
          label="Mode compact (densité élevée)"
          description="Réduit les espaces entre les éléments"
          checked={ds.compactMode}
          onChange={ds.setCompactMode}
        />
        <FullToggle
          icon={<Zap className="h-4 w-4" />}
          label="Animations réduites"
          description="Pour les utilisateurs sensibles au mouvement"
          checked={ds.reducedMotion}
          onChange={ds.setReducedMotion}
        />
      </div>
    </div>
  );
}

// ── Helpers ──

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {children}
    </span>
  );
}

function CompactToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-2 min-h-[32px] px-0.5">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} className="scale-75" />
    </div>
  );
}

function FullToggle({
  icon, label, description, checked, onChange,
}: {
  icon: React.ReactNode; label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3 min-h-[44px]">
      <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center text-muted-foreground shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <Label className="text-sm font-medium text-foreground cursor-pointer">{label}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/** Compact inline advanced unlock panel */
function AdvancedUnlockMini({
  requirements,
  completedCount,
  totalCount,
  navigate,
}: {
  requirements: { id: string; label: string; met: boolean; current: number; target: number; actionRoute: string; actionLabel: string }[];
  completedCount: number;
  totalCount: number;
  navigate: (path: string) => void;
}) {
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-2.5 space-y-2 mt-1">
      <div>
        <h4 className="text-[11px] font-semibold text-foreground">Débloquer le mode Avancé</h4>
        <p className="text-[9px] text-muted-foreground mt-0.5">
          Maîtrise les outils de base pour accéder aux fonctionnalités avancées.
        </p>
      </div>
      <Progress value={(completedCount / Math.max(totalCount, 1)) * 100} className="h-1.5" />
      <p className="text-[9px] text-muted-foreground text-right">{completedCount} / {totalCount}</p>
      <div className="space-y-1">
        {requirements.map((req) => (
          <div key={req.id} className="flex items-center gap-1.5">
            {req.met ? (
              <CheckCircle2 className="h-3 w-3 text-primary shrink-0" />
            ) : (
              <Circle className="h-3 w-3 text-muted-foreground/50 shrink-0" />
            )}
            <span className={cn("text-[10px] flex-1", req.met ? "text-muted-foreground line-through" : "text-foreground font-medium")}>
              {req.label}
              {!req.met && req.target > 1 && <span className="text-muted-foreground font-normal ml-0.5">({req.current}/{req.target})</span>}
            </span>
            {!req.met && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(req.actionRoute); }}
                className="text-[9px] text-primary font-medium hover:underline shrink-0 flex items-center gap-0.5 min-h-[44px]"
              >
                {req.actionLabel}
                <ArrowRight className="h-2 w-2" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
